/**
 * The start view's filter row (Option C, C1 + C3; split out of `EngineStartView` for the final UX review): the search
 * icon, the filter — an editable combobox (`useEngineCombobox`) — and the key hints. It stays at the top of the scroll
 * region (sticky, so it also stays in the very-short tier).
 *
 * A pasted connection string collapses into a chip (UX-03): the input beside it is empty, so the string (a password in
 * it included) is never on screen, while the dialog keeps it for Continue. The paste never enters the input at all: it is
 * taken from the clipboard event and the browser's insertion cancelled (`takePaste`, uxfix review UXF-01); so is a
 * dropped text (`takeDrop`). Typing or pasting replaces it; Backspace or Delete in the empty input, or the chip's ✕,
 * clears it. A connection string pasted (or dropped) beside a filter term — All engines keeps "postg" — replaces the term:
 * it is read on its own, never joined onto it. The key hints show only the keys that do something (UX-13):
 * Enter under a detection card, none while the card asks which engine, else ↑ ↓ Enter.
 */
import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FilterVia } from '../hooks/useDialogNavigation';
import { PastedStringChip } from '../paste/PastedStringChip';
import { takeDrop, takePaste, usePasteCaret, type TakenPaste } from '../paste/takePaste';
import { looksLikeConnectionString } from '../paste/pastePreview';

export const FILTER_CHIP_ID = 'engine-filter-chip';

type Props = {
  filter: string;
  onFilterChange: (text: string, via: FilterVia) => void;
  /** The chip's label while the filter holds a pasted connection string; null: the text is shown. */
  chip: string | null;
  onClearChip: (via: 'button' | 'key') => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  activeDescendant: string | undefined;
  expanded: boolean;
  keys: 'navigate' | 'continue' | 'none';
  filterRef: RefObject<HTMLInputElement>;
  short: boolean;
};

function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-b-2 border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-none text-muted-foreground">{children}</kbd>;
}

export function StartFilterRow({ filter, onFilterChange, chip, onClearChip, onKeyDown, activeDescendant, expanded, keys, filterRef, short }: Props) {
  const placeCaret = usePasteCaret(filterRef);
  // A paste, or a text dropped on the input, never enters it (UXF-01): the dialog takes it as a paste.
  const taken = (paste: TakenPaste | null) => {
    if (!paste) return;
    // A filter term is inserted at the caret, as a paste would; a connection string stands alone (release pass: it was
    // joined onto the kept term and failed to parse).
    const placed = looksLikeConnectionString(paste.pasted) ? { ...paste, value: paste.pasted, caret: paste.pasted.length } : paste;
    placeCaret(placed);
    onFilterChange(placed.value, 'paste');
  };
  return (
    <div data-flip-key="filter" className={cn('sticky top-0 z-10 bg-surface-raised', short ? 'py-2' : 'pb-2 pt-4')}>
      <div className="flex h-9 items-center gap-2.5 rounded-md border border-input bg-background pl-3 pr-1.5 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/20">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        {chip !== null && (
          <PastedStringChip
            id={FILTER_CHIP_ID}
            label={chip}
            onClear={() => {
              onClearChip('button');
              filterRef.current?.focus();
            }}
          />
        )}
        <input
          ref={filterRef}
          id="engine-filter"
          type="text"
          role="combobox"
          aria-label="Filter engines or paste a connection string"
          aria-controls="connection-start-options"
          aria-expanded={expanded}
          aria-autocomplete="list"
          aria-describedby={chip !== null ? `${FILTER_CHIP_ID} engine-filter-hint` : 'engine-filter-hint'}
          aria-activedescendant={activeDescendant}
          autoComplete="off"
          spellCheck={false}
          placeholder={chip !== null ? '' : 'Filter engines or paste a connection string'}
          className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          // The chip stands for the string: the input shows none of it (the dialog keeps it).
          value={chip !== null ? '' : filter}
          onPaste={(event) => taken(takePaste(event))}
          onDrop={(event) => taken(takeDrop(event))}
          onChange={(event) => onFilterChange(event.target.value, 'type')}
          onKeyDown={(event) => {
            if (chip !== null && (event.key === 'Backspace' || event.key === 'Delete') && !event.nativeEvent.isComposing) {
              event.preventDefault();
              onClearChip('key');
              return;
            }
            onKeyDown(event);
          }}
          data-testid="engine-filter-input"
        />
        {keys !== 'none' && (
          <span className="hidden items-center gap-1 sm:flex" aria-hidden>
            {keys === 'navigate' && (
              <>
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
              </>
            )}
            <Kbd>Enter</Kbd>
          </span>
        )}
      </div>
    </div>
  );
}
